/* Existing ms6 data / search destinations; no API calls, no DOM relocation. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('ms-home-vehicle-search')) return;

  class MSHomeVehicleSearch extends HTMLElement {
    connectedCallback() {
      // Defer one microtask for theme-editor insertions whose children are still parsing.
      queueMicrotask(() => { if (this.isConnected && !this.controller) this.initialize(); });
    }
    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }
    initialize() {
      const keys = ['make', 'model', 'year', 'chassis', 'engine', 'details'];
      this.fields = Object.fromEntries(keys.map(key => [key, this.querySelector(`[data-ms-field="${key}"]`)]));
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-ms-submit]');
      this.status = this.querySelector('[data-ms-status]');
      if (!this.form || !this.button || keys.some(key => !this.fields[key])) return;
      try {
        const source = this.querySelector('[data-ms-vehicle-data]');
        const data = JSON.parse(source ? source.textContent : 'null');
        if (!data || !data.vehicles || !data.fits || !Object.keys(data.vehicles).length) throw new Error('missing data');
        this.vehicles = data.vehicles;
        this.fits = data.fits;
      } catch (_) {
        this.showStatus('Vehicle selection is temporarily unavailable. Please use Browse all parts.');
        keys.forEach(key => { this.fields[key].disabled = true; });
        this.button.disabled = true;
        return;
      }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.fill('make', Object.keys(this.vehicles).sort());
      ['model', 'year', 'chassis', 'engine', 'details'].forEach(key => this.fill(key, []));
      this.button.disabled = false;
      this.showStatus('');
      this.form.addEventListener('change', event => this.onChange(event), options);
      this.form.addEventListener('submit', event => this.onSubmit(event), options);
    }
    fill(key, values) {
      const field = this.fields[key];
      const label = key.charAt(0).toUpperCase() + key.slice(1);
      field.replaceChildren(new Option(`- Select ${label} -`, ''));
      [...new Set(values)].forEach(value => field.add(new Option(String(value), String(value))));
      field.disabled = !values.length;
      field.removeAttribute('aria-invalid');
    }
    onChange(event) {
      const key = event.target.dataset.msField;
      if (!Object.prototype.hasOwnProperty.call(this.fields, key)) return;
      if (event.target.value) event.target.removeAttribute('aria-invalid');
      const { make, model, year } = this.fields;
      this.showStatus('');
      if (key === 'make') {
        ['model', 'year', 'chassis', 'engine', 'details'].forEach(item => this.fill(item, []));
        const models = this.vehicles[make.value];
        if (models) this.fill('model', Object.keys(models).sort());
      } else if (key === 'model') {
        ['year', 'chassis', 'engine', 'details'].forEach(item => this.fill(item, []));
        const generations = (this.vehicles[make.value] || {})[model.value] || [];
        this.fill('year', [...new Set(generations.flatMap(item => item.years))].sort((a, b) => b - a));
      } else if (key === 'year') {
        ['chassis', 'engine', 'details'].forEach(item => this.fill(item, []));
        const generations = ((this.vehicles[make.value] || {})[model.value] || [])
          .filter(item => item.years.includes(parseInt(year.value, 10)));
        this.fill('chassis', generations.flatMap(item => item.chassis));
        this.fill('engine', generations.flatMap(item => item.engines));
        this.fill('details', generations.flatMap(item => item.trims));
      }
    }
    onSubmit(event) {
      event.preventDefault();
      const { make, model, year, chassis, engine, details } = this.fields;
      if (!make.value || !model.value) {
        this.showStatus('Please select at least Make and Model.');
        const missing = !make.value ? make : model;
        missing.setAttribute('aria-invalid', 'true');
        missing.focus();
        return;
      }
      // Preserve the legacy routing exactly. Make/model mapping is NOT detailed fitment validation.
      const id = (this.fits[make.value] || {})[model.value];
      let url;
      if (id) {
        url = '/collections/all?filter.p.m.custom.fits_vehicles=' + encodeURIComponent('gid://shopify/Metaobject/' + id);
      } else {
        const query = [make.value, model.value, year.value, chassis.value, engine.value, details.value].filter(Boolean).join(' ');
        this.querySelector('[data-ms-query]').value = query;
        url = '/search?q=' + encodeURIComponent(query) + '&type=product';
      }
      window.location.assign(url);
    }
    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }
  }
  customElements.define('ms-home-vehicle-search', MSHomeVehicleSearch);
})();
